import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Download } from "@mui/icons-material";

const InvoicePage = () => {
  const { accountId, entryId } = useParams();
  const [searchParams] = useSearchParams();
  const isOverdue = searchParams.get('type') === 'overdue';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        let url = `https://us-central1-finaculator.cloudfunctions.net/getPublicInvoiceData?accountId=${accountId}`;
        if (entryId) url += `&entryId=${entryId}`;
        
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load invoice');
        
        const result = await res.json();
        setData(result);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Could not load invoice data.');
        setLoading(false);
      }
    };
    fetchInvoiceData();
  }, [accountId, entryId]);

  const currencyFormat = (val) => Number(val).toLocaleString('en-IN', { maximumFractionDigits: 3 });

  const downloadPDF = () => {
    const input = document.getElementById("invoice-container");
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice_${accountId.split('_')[0]}.pdf`);
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f7fa', color: '#333' }}>
        <p>Loading document...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f7fa', color: '#d32f2f' }}>
        <p>{error || 'Document not found.'}</p>
      </div>
    );
  }

  const { financier, customer, account, entry } = data;
  
  let totalPaid = 0;
  if (account.allEntries) {
    totalPaid = Object.values(account.allEntries).reduce((acc, e) => acc + (e.isUpfrontInterest ? 0 : Number(e.amountPaid)), 0);
  }
  const balance = account.amount - totalPaid;

  return (
    <div style={{ minHeight: '100vh', background: '#e0e5ec', padding: '20px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      
      <div style={{ width: '100%', maxWidth: 700, display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 20 }}>
        <button 
          onClick={downloadPDF}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#15C358', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
        >
          <Download fontSize="small" /> Download PDF
        </button>
      </div>

      <div 
        id="invoice-container"
        style={{
          width: '100%', maxWidth: 700, background: '#fff', padding: '40px',
          borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          position: 'relative', overflow: 'hidden', color: '#333', fontFamily: '"Inter", sans-serif'
        }}
      >
        {entry ? (
          <div style={{
            position: 'absolute', top: 60, right: -40, transform: 'rotate(35deg)',
            border: '4px solid #4caf50', color: '#4caf50', padding: '8px 40px',
            fontSize: 24, fontWeight: 900, letterSpacing: 4, opacity: 0.3
          }}>
            PAID
          </div>
        ) : isOverdue ? (
          <div style={{
            position: 'absolute', top: 60, right: -60, transform: 'rotate(35deg)',
            border: '4px solid #f44336', color: '#f44336', padding: '8px 40px',
            fontSize: 24, fontWeight: 900, letterSpacing: 4, opacity: 0.2
          }}>
            OVERDUE
          </div>
        ) : (
          <div style={{
            position: 'absolute', top: 60, right: -60, transform: 'rotate(35deg)',
            border: '4px solid #15C358', color: '#15C358', padding: '8px 40px',
            fontSize: 24, fontWeight: 900, letterSpacing: 4, opacity: 0.1
          }}>
            STATEMENT
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #f0f0f0', paddingBottom: 20, marginBottom: 30 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, color: '#1a1a1a', fontWeight: 800 }}>{financier.name}</h1>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>{financier.contact}</p>
            {financier.address && <p style={{ margin: '2px 0 0', color: '#666', fontSize: 13, maxWidth: 250 }}>{financier.address}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ margin: 0, fontSize: 22, color: isOverdue ? '#f44336' : '#15C358', textTransform: 'uppercase' }}>
              {entry ? 'Payment Receipt' : isOverdue ? 'Overdue Notice' : 'Receipt of Account Initiated'}
            </h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
              Date: {entry ? new Date(entry.date).toLocaleDateString() : new Date().toLocaleDateString()}
            </p>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
              Account: {account.accountId.split('_')[0]}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '60px', marginBottom: 40 }}>
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#999', textTransform: 'uppercase', letterSpacing: 1 }}>From:</h3>
            <h2 style={{ margin: 0, fontSize: 16, color: '#333' }}>{financier.name}</h2>
          </div>
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#999', textTransform: 'uppercase', letterSpacing: 1 }}>Billed To:</h3>
            <h2 style={{ margin: 0, fontSize: 18, color: '#333' }}>{customer.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#555', fontSize: 14 }}>{customer.contact}</p>
            {customer.address && <p style={{ margin: '4px 0 0', color: '#777', fontSize: 14, maxWidth: 300 }}>{customer.address}</p>}
          </div>
        </div>

        <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', marginBottom: 30 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid #eee', color: '#666', fontWeight: 600, fontSize: 13 }}>Description</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid #eee', color: '#666', fontWeight: 600, fontSize: 13, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14 }}>Principal Amount</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, textAlign: 'right', fontWeight: 500 }}>₹ {currencyFormat(account.amount)}</td>
              </tr>
              {((account.installmentAmount * account.installmentPeriod) - (Number(account.amount) + Number(account.interestAmount || 0))) !== 0 && (
                <tr>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, color: '#666' }}>Round Off Adjustment</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, textAlign: 'right', fontWeight: 500, color: '#666' }}>₹ {currencyFormat((account.installmentAmount * account.installmentPeriod) - (Number(account.amount) + Number(account.interestAmount || 0)))}</td>
                </tr>
              )}
              {entry && (
                <tr style={{ background: '#f2f9f1' }}>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, color: '#2e7d32', fontWeight: 600 }}>{entry.isUpfrontInterest ? 'Upfront Interest Collected' : 'Payment Received (Current)'}</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, textAlign: 'right', color: '#2e7d32', fontWeight: 700 }}>₹ {currencyFormat(entry.amountPaid)}</td>
                </tr>
              )}
              {isOverdue && (
                <tr style={{ background: '#fdf3f2' }}>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, color: '#c62828', fontWeight: 600 }}>Expected Installment ({account.installmentType})</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontSize: 14, textAlign: 'right', color: '#c62828', fontWeight: 700 }}>₹ {currencyFormat(account.installmentAmount)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666', fontSize: 14 }}>Total Recovered:</span>
              <span style={{ fontWeight: 600 }}>₹ {currencyFormat(totalPaid)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
              <span style={{ color: '#333', fontSize: 16, fontWeight: 700 }}>Outstanding Balance:</span>
              <span style={{ fontWeight: 700, fontSize: 18, color: balance > 0 ? '#f44336' : '#4caf50' }}>₹ {currencyFormat(balance)}</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 60, textAlign: 'center', color: '#aaa', fontSize: 12, borderTop: '1px solid #eee', paddingTop: 20 }}>
          {isOverdue 
            ? "Please clear your pending dues at the earliest to avoid additional penalties. Ignore if already paid." 
            : "Thank you for your business. This is a system generated document."}
          <div style={{ marginTop: 8, fontWeight: 'bold' }}>Powered by Finaculator</div>
        </div>

      </div>
    </div>
  );
};

export default InvoicePage;
